import React, { useMemo } from 'react';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import InventoryMonitorsEdit from '@shared/inventory_monitors/InventoryMonitorsEdit';
import { useParams } from 'react-router-dom';

import { inventoryMonitorService } from '@services/operator/inventoryMonitorService';

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: {
    id: string;
    name?: string;
    code?: string;
    [key: string]: unknown;
  };
}

const OperatorInventoryMonitorsEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { merchantInventoryTypes, merchants, currentMerchant } = useOperatorData();
  const productService = useOperatorProductService();
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();

  // Edit mode 下，useRecordMerchantSync 已把 currentMerchant 切到 record 所屬 merchant；
  // New mode 下，外層的 <MerchantSelect> 確保 currentMerchant 已選定。
  // Reference data 永遠跟著 currentMerchant 走。
  const effectiveMerchantId = currentMerchant?.id ?? null;

  const merchantOptions = useMemo(
    () => (merchants ?? []).map(m => ({ value: m.id, label: m.name })),
    [merchants]
  );

  const inventoryTypes = useMemo(() => {
    if (!effectiveMerchantId) return [];
    return ((merchantInventoryTypes as MerchantInventoryType[] | undefined) ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  // 編輯模式：wrapper 透過 onInventoryMonitorLoaded 拿到 record 的 merchant_id，
  // 同步 OperatorDataContext.currentMerchant；切換期間 merchantDataLoading=true
  // 由 shared 的 <Spin> 擋住表單渲染，避免閃錯的下拉。
  return (
    <InventoryMonitorsEdit
      inventoryMonitorService={inventoryMonitorService}
      productService={productService}
      inventoryTypes={inventoryTypes}
      inventoryMonitorId={id}
      merchantOptions={merchantOptions}
      role="operator"
      onInventoryMonitorLoaded={handleRecordLoaded}
      merchantDataLoading={merchantDataLoading}
      // 編輯模式不會用到（form 內已塞了 record 的 merchant_id）；
      // 新建模式由外層 <MerchantSelect> 包裝的 New.jsx 走另一條路。
      merchantIdForCreate={effectiveMerchantId}
    />
  );
};

export default OperatorInventoryMonitorsEdit;
